import * as React from "react";
import { Box, Button, Typography } from "@mui/material";
import { useTheme } from "@mui/material";
import { tokens } from "../../theme";
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import TextField from '@mui/material/TextField';
import Axios from "axios";
import { Editor } from '@tinymce/tinymce-react';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import { getAuthHeader } from '../../utils/authToken';

const CustomWidthTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))({
    [`& .${tooltipClasses.tooltip}`]: {
        maxWidth: 214,
    },
});

const options = {
    height: 400,
    menubar: false,
    plugins: [
        'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
        'anchor', 'searchreplace', 'visualblocks', 'fullscreen',
        'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
    ],
    toolbar: [
        ' undo redo | blocks | bold italic forecolor backcolor fontsize | fontselect |' +
        'alignleft aligncenter alignright alignjustify | lineheight |' +
        'bullist numlist outdent indent | removeformat | help'
    ],
    font_formats: 'Arial=arial,helvetica,sans-serif;' +
                 'Arial Black=arial black,avant garde;' +
                 'Courier New=courier new,courier;' +
                 'Georgia=georgia,palatino;' +
                 'Tahoma=tahoma,arial,helvetica,sans-serif;' +
                 'Times New Roman=times new roman,times;' +
                 'Verdana=verdana,geneva;' +
                 'Impact=impact,chicago;' +
                 'Comic Sans MS=comic sans ms,sans-serif;' +
                 'Trebuchet MS=trebuchet ms,geneva;',
    htmlAllowedTags:  ['.*'],
    htmlAllowedAttrs: ['.*'],
    extended_valid_elements: '*[.*]',
    content_style: "p { margin: 0; }",
};

const TemplateSearchModal = ({ 
    semesters,
    season,
    semester_id,
    semester_name,
    course_type,
    setErrorAlertMessage,
    setErrorAlertOpen,
    setSuccessAlertMessage,
    setSuccessAlertOpen,
    handleLogout,
    setUpdated,
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        minWidth: 940,
        height: '80vh',
        bgcolor: colors.primary[600],
        border: '2px solid grey',
        boxShadow: 24,
        p: 4,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
    };

    const [open, setOpen] = React.useState(false);
    const handleOpen = () => {
        // focus on the input field
        setTimeout(() => {
            const inputField = document.getElementById('outlined-controlled');
            if (inputField) {
                inputField.focus();
            }
        }, 100);

        setOpen(true);
    };
    const handleClose = () => {
        setCourseName("");
        setCourseList([]);
        setSelectedTemplate(null);
        setSelectedCourse(null);
        setOpen(false);
    };

    const [courseName, setCourseName] = React.useState("");
    const [courseList, setCourseList] = React.useState([]);
    const [selected_template, setSelectedTemplate] = React.useState(null);
    const [selected_course, setSelectedCourse] = React.useState(null);

    React.useEffect(() => {
        // key event listener to close modal on escape key
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                handleClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const handleSearch = async () => {
        try {
            const response = await Axios.get(`${process.env.REACT_APP_URL}/api/v1/potential-courses-search`, {
                params: {
                    courseName: courseName,
                },
                headers: {
                    Authorization: getAuthHeader(),
                },
            });

            const courseList = await response.data || [];
            setCourseList(courseList);
            if (!courseList || courseList.length === 0) {
                setErrorAlertMessage("No courses found");
                setErrorAlertOpen(true);
            } else {
                setSuccessAlertMessage("Courses fetched successfully");
                setSuccessAlertOpen(true);
            }
        } catch (err) {
            console.error(err);
            setErrorAlertMessage("Error fetching courses");
            setErrorAlertOpen(true);
        }
    };

    const fetchCourseWithPotentialSchedules = async (courseId) => {
        return new Promise(async (resolve, reject) => {
            try {
                const response = await Axios.get(`${process.env.REACT_APP_URL}/api/v1/potential-courses/${courseId}`, {
                    headers: {
                        Authorization: getAuthHeader(),
                    },
                });

                const course = response.data;
                if (!course) {
                    setErrorAlertMessage("Course not found");
                    setErrorAlertOpen(true);
                    resolve(null);
                }

                console.log("Course with potential schedules: ", course);

                resolve(course);
            } catch (error) {
                console.error("Error fetching course with potential schedules:", error);
                setErrorAlertMessage("Error fetching course details");
                setErrorAlertOpen(true);
                resolve(null);
            }
        });
    }

    const buildCourseToCopy = (course, template) => {
        if (!course || !template) {
            setErrorAlertMessage("Invalid course or template");
            setErrorAlertOpen(true);
            return null;
        }

        if (!course.course_potential_schedules || course.course_potential_schedules.length === 0) {
            setErrorAlertMessage("No potential schedules found for this course");
            setErrorAlertOpen(true);
            return null;
        }

        return {
            id: course.id,
            code: course.code,
            course_title: course.course_title,
            is_potential_course: course.is_potential_course,
            is_private_course: course.is_private_course,
            potential_course_type: course.potential_course_type,
            subject: course.subject,
            course_potential_schedule: {
                category: course.course_potential_schedules[0].category,
                days: course.course_potential_schedules[0].days,
                email_template: template,
                order: course.course_potential_schedules[0].order,
                _subject: course.course_potential_schedules[0].subject || "",
                teacher_id: course.course_potential_schedules[0].teacher_id,
                potential_schedule: {
                    start_time: course.course_potential_schedules[0].potential_schedule.start_time,
                    end_time: course.course_potential_schedules[0].potential_schedule.end_time,
                    semester_id: semester_id,
                }
            }
        };
    }

    const handleTemplateSelect = async (course) => {
        if (!course) {
            setErrorAlertMessage("No course selected");
            setErrorAlertOpen(true);
            return;
        }

        const course_potential_schedules = course.course_potential_schedules || [];
        if (course_potential_schedules.length === 0) {
            setErrorAlertMessage("No Template found for this course");
            setErrorAlertOpen(true);
            return;
        }

        for (let i = 0; i < course_potential_schedules.length; i++) {
            const schedule = course_potential_schedules[i];

            const courseWithSchedules = await fetchCourseWithPotentialSchedules(course.id);
            if (!courseWithSchedules) {
                setErrorAlertMessage("Error fetching course with potential schedules");
                setErrorAlertOpen(true);
                return;
            }

            if (schedule.email_template !== undefined && schedule.email_template !== null) {
                setSelectedTemplate(schedule.email_template);
                setSelectedCourse(courseWithSchedules);
                return;
            } else {
                setErrorAlertMessage("No Template found for this course");
                setErrorAlertOpen(true);
                setSelectedTemplate("No Template found for this course");
                setSelectedCourse(courseWithSchedules);
                return;
            }
        }
    }

    const handleCopySchedule = async () => {
        // create a new set of courses to copy
        if (!selected_template || !selected_course) {
            setErrorAlertMessage("No course and template selected to copy");
            setErrorAlertOpen(true);
            return;
        }

        const course_to_copy = buildCourseToCopy(selected_course, selected_template);

        await Axios.post(`${process.env.REACT_APP_URL}/api/v1/potential-courses/copy-schedule`, {
            courses_to_copy: [course_to_copy],
            semester_id: semester_id,
            course_type: course_type,
        }, {
            headers: {
                Authorization: getAuthHeader(),
            },
        }).then((response) => {
            setSuccessAlertMessage("Schedule copied successfully!");
            setSuccessAlertOpen(true);
            setUpdated(true);
            handleClose();
        }).catch((error) => {
            console.log("error: ", error);
            if (error.response?.status === 401) {
                handleLogout();
                return;
            }
            setErrorAlertMessage(error.response?.data?.message || error.response?.data?.error || "Something went wrong!");
            setErrorAlertOpen(true);
        });
    };

    const getSemester = (course) => {
        if (!course || !course.semester) {
            return "Unknown Semester";
        }

        return `${course.semester?.year} ${season[course.semester?.season]}`
    }

    const getCourseType = (course) => {
        if (!course) {
            return "Unknown Course Type";
        }

        /**
         * potential_course_type is only used when is_potential_course is true
         * this indicates the course is for
         * null. group potential course,
         * 1. private potential course,
         * 2. consulting email and principal consultation email
         */

        if (course.potential_course_type === null) {
            return "Group";
        } else if (course.potential_course_type === 1) {
            return "Private";
        } else if (course.potential_course_type === 2) {
            return "Other";
        }

        return "Unknown Course Type";
    }

    return (
        <>
            <Button
                variant="contained"
                color="primary"
                sx={{ 
                    ml: "auto",
                }}
                onClick={() => handleOpen()}
            >
                Search Template
            </Button>
            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Fade in={open}>
                    <Box sx={style}>
                        <Typography
                            variant="h6"
                            sx={{
                                color: colors.primary[100],
                                fontSize: 24,
                                marginBottom: 2,
                            }}
                        >
                            Search Template
                        </Typography>
                        <Box
                            sx={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 2,
                                    marginBottom: 2,
                                }}
                            >
                                <TextField
                                    id="outlined-controlled"
                                    label="Type course name to search"
                                    value={courseName}
                                    onChange={(event) => {
                                        setCourseName(event.target.value);
                                    }}
                                    sx={{
                                        width: "300px",
                                    }}
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                        handleSearch();
                                    }}
                                >
                                    Search
                                </Button>
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    flexWrap: "wrap",
                                    gap: 2,
                                }}
                            >
                                {courseList.map((course, index) => (
                                    <Button
                                        key={index}
                                        variant="outlined"
                                        onClick={() => handleTemplateSelect(course)}
                                        sx={
                                            course.id === selected_course?.id ? {
                                                backgroundColor: colors.primary[900],
                                            } : {}
                                        }
                                    >
                                        <Typography
                                            variant="h6"
                                            sx={{ color: colors.primary[100] }}
                                        >
                                            {course.course_title} ({getSemester(course)}) ({getCourseType(course)})
                                        </Typography>
                                    </Button>
                                ))}
                            </Box>
                            {selected_template && (
                                <Box
                                    sx={{
                                        marginTop: 2,
                                    }}
                                >
                                    <Editor
                                        apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
                                        init={options}
                                        value={selected_template}
                                        onEditorChange={
                                            (newValue, editor) => {
                                                setSelectedTemplate(newValue);
                                            }
                                        }
                                    />
                                </Box>
                            )}

                            <CustomWidthTooltip 
                                title={"This will not copy 'combined course' settings, which means that you will have to set up the combined course again."}
                                placement="top" 
                                arrow
                            >
                                <Button
                                    variant="contained"
                                    color="primary"
                                    sx={{ 
                                        mt: "auto",
                                        ml: "auto",
                                        mr: "auto",
                                        minWidth: 200,
                                    }}
                                    onClick={() => {handleCopySchedule();}}
                                >
                                    Copy to {semester_name}
                                </Button>
                            </CustomWidthTooltip>
                        </Box>
                    </Box>
                </Fade>
            </Modal>
        </>
    );
};

export default TemplateSearchModal